/**
 * Los pasos de instalación, dibujados.
 *
 * @module components/install/InstallStepsCard
 * @version 6.11.43.00
 *
 * Cada paso muestra el número, la acción en criollo y un "chip" con la
 * etiqueta EXACTA que el jugador va a ver en su navegador. La idea es que
 * pueda comparar el chip con la pantalla en vez de leer un párrafo.
 */

import React from 'react';
import { MoreHorizontal, MoreVertical, Share, Home, Plus, Check, Lightbulb } from 'lucide-react';
import type { InstallGuide, InstallStep, InstallStepIcon } from '../../utils/installSteps';

const STEP_ICONS: Record<InstallStepIcon, React.ComponentType<{ size?: number; className?: string }>> = {
  dots: MoreVertical,
  share: Share,
  more: MoreHorizontal,
  home: Home,
  plus: Plus,
  check: Check,
};

interface InstallStepsCardProps {
  guide: InstallGuide;
  /** Título de la tarjeta. Por defecto: "Así se instala en {navegador}". */
  title?: string;
  className?: string;
}

/** Un paso: número + texto + chip con la etiqueta literal del menú. */
const StepRow: React.FC<{ index: number; step: InstallStep }> = ({ index, step }) => {
  // Los chips de puntitos ya SON el ícono: repetirlo al lado es ruido.
  const isGlyphChip = step.chip === '⋮' || step.chip === '•••' || step.chip === '≡';
  const Icon = step.icon && !isGlyphChip ? STEP_ICONS[step.icon] : null;

  return (
    <li className="flex items-start gap-3">
      <span
        aria-hidden="true"
        className="w-7 h-7 rounded-full bg-primary text-white font-display font-black text-sm flex items-center justify-center flex-shrink-0 shadow-sm"
      >
        {index + 1}
      </span>

      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-[15px] leading-snug text-text-main">
          <span className="font-medium">{step.text}</span>{' '}
          {step.chip && (
            <span
              className={`inline-flex items-center gap-1.5 align-middle rounded-lg border-2 border-primary/20 bg-white px-2 py-1 font-bold text-primary-dark shadow-sm ${
                isGlyphChip ? 'text-lg leading-none tracking-widest px-2.5' : 'text-sm'
              }`}
            >
              {Icon && <Icon size={15} className="text-primary flex-shrink-0" />}
              {step.chip}
            </span>
          )}
        </p>
        {step.hint && (
          <p className="text-xs text-text-muted mt-1 leading-snug">{step.hint}</p>
        )}
      </div>
    </li>
  );
};

export const InstallStepsCard: React.FC<InstallStepsCardProps> = ({
  guide,
  title,
  className = '',
}) => (
  <div className={`rounded-2xl border-2 border-primary/15 bg-primary/[0.04] p-5 ${className}`}>
    <p className="font-display font-black uppercase tracking-tight text-primary-dark text-base mb-1">
      {title ?? `Así se instala en ${guide.browserLabel}`}
    </p>
    <p className="text-xs text-text-muted mb-4">
      Son {guide.steps.length} toques. Te esperamos del otro lado.
    </p>

    <ol className="space-y-4">
      {guide.steps.map((step, i) => (
        <StepRow key={`${step.text}-${step.chip ?? i}`} index={i} step={step} />
      ))}
    </ol>

    <div className="mt-4 flex items-start gap-2 rounded-xl bg-secondary/25 border border-secondary-dark/20 p-3">
      <span aria-hidden="true" className="text-lg leading-none">🎾</span>
      <p className="text-[13px] font-semibold text-text-main leading-snug">{guide.closing}</p>
    </div>

    {guide.note && (
      <div className="mt-3 flex items-start gap-2 text-xs text-text-muted leading-snug">
        <Lightbulb size={14} className="text-warning flex-shrink-0 mt-0.5" />
        <p>{guide.note}</p>
      </div>
    )}
  </div>
);

export default InstallStepsCard;
